/**
 * Banner rotativo. O mesmo HTML vale na visualização e no Moodle.
 * O palco usa as mesmas classes em RenderBannerCarousel.
 */

function escText(value) {
  return String(value ?? '').replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
}

function escAttr(value) {
  return escText(value).replace(/"/g, '&quot;');
}

function clampInt(value, min, max, fallback) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function safeColor(value, fallback) {
  const v = String(value || '').trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  return fallback;
}

function safeUrl(value) {
  const url = String(value || '').trim();
  if (!url || /^(javascript|data):/i.test(url)) return '';
  return url;
}

function hexToRgba(hex, transparencyPct) {
  const raw = String(hex || '').replace('#', '').trim();
  const full = raw.length === 3 ? raw.split('').map((ch) => ch + ch).join('') : raw;
  const n = parseInt(full, 16);
  const alpha = Math.max(0, Math.min(1, 1 - (clampInt(transparencyPct, 0, 100, 0) / 100)));
  if (!Number.isFinite(n) || full.length !== 6) return `rgba(15,23,42,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

function safeId(widgetId) {
  return String(widgetId || 'banner').replace(/[^a-zA-Z0-9_-]/g, '') || 'banner';
}

export function bannerView(content) {
  const c = content || {};
  const slides = Array.isArray(c.slides) && c.slides.length
    ? c.slides.slice(0, 12)
    : [{ url: '', caption: '' }];
  const align = c.textAlign === 'right' || c.textAlign === 'center' ? c.textAlign : 'left';
  const heightMode = c.heightMode === 'image' ? 'image' : 'fixed';
  return {
    slides,
    heightMode,
    heightPx: clampInt(c.heightPx, 120, 900, 320),
    span: c.span === 'full' ? 'full' : 'column',
    accent: safeColor(c.accentColor, '#3b82f6'),
    align,
    textWidth: clampInt(c.textWidth, 20, 100, 46),
    textPad: clampInt(c.textPad, 0, 80, 28),
    titleSubGap: clampInt(c.titleSubGap ?? c.textGap, 0, 80, 10),
    subDescGap: clampInt(c.subDescGap ?? c.textGap, 0, 80, 10),
    radiusPx: clampInt(c.radiusPx, 0, 80, 0),
    textBg: hexToRgba(safeColor(c.textBg, '#0f172a'), c.textBgAlpha ?? 42),
    titleSize: clampInt(c.titleSize, 16, 84, 36),
    titleColor: safeColor(c.titleColor, '#ffffff'),
    subtitleSize: clampInt(c.subtitleSize, 12, 48, 18),
    subtitleColor: safeColor(c.subtitleColor, '#e2e8f0'),
    descSize: clampInt(c.descSize, 12, 36, 15),
    descColor: safeColor(c.descColor, '#f8fafc'),
    btnBg: safeColor(c.btnBg, '#ffffff'),
    btnColor: safeColor(c.btnColor, '#0f172a'),
    btn2Color: safeColor(c.btn2Color, '#ffffff'),
    floats: (Array.isArray(c.floats) ? c.floats : []).slice(0, 8).map((item) => ({
      url: safeUrl(item?.url),
      x: clampInt(item?.x, 0, 100, 70),
      y: clampInt(item?.y, 0, 100, 20),
      width: clampInt(item?.width, 40, 640, 180),
      motion: ['float', 'sway', 'pulse'].includes(item?.motion) ? item.motion : 'none',
      layer: item?.layer === 'front' ? 'front' : 'back',
    })).filter((item) => item.url),
  };
}

/** Grade só com banner, ou com banner na largura da página: sem o respiro padrão da linha. */
export function rowFlushForBanner(row) {
  const widgets = (row?.columns || []).flatMap((col) => (
    (col?.widgets || []).filter((w) => w && w.type !== 'page-break' && w.type !== 'progress-bar' && w.type !== 'header-menu')
  ));
  if (!widgets.some((w) => w.type === 'carousel')) return false;
  if (widgets.some((w) => w.type === 'carousel' && w.content?.span === 'full')) return true;
  return widgets.every((w) => w.type === 'carousel');
}

export function slideCopy(slide) {
  const title = String(slide?.title || '').trim();
  const subtitle = String(slide?.subtitle || '').trim();
  const description = String(slide?.description || '').trim();
  const caption = String(slide?.caption || '').trim();
  const btn1Label = String(slide?.btn1Label || '').trim();
  const btn2Label = String(slide?.btn2Label || '').trim();
  const hasFields = !!(title || subtitle || description || btn1Label || btn2Label);
  return {
    url: safeUrl(slide?.url),
    title: title || (hasFields ? caption : ''),
    subtitle,
    description,
    caption: hasFields ? '' : caption,
    btn1Label,
    btn1Link: safeUrl(slide?.btn1Link) || (btn1Label ? '#' : ''),
    btn2Label,
    btn2Link: safeUrl(slide?.btn2Link) || (btn2Label ? '#' : ''),
    hasOverlay: hasFields || !!title,
  };
}

export const BANNER_CSS = `
.madri-banner{position:relative;box-sizing:border-box;width:100%;max-width:100%;border-radius:var(--bn-radius,0px);overflow:hidden;background:#0f172a;color:#fff}
.madri-banner *{box-sizing:border-box}
.madri-banner--full{width:100vw;max-width:100vw;margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw)}
.madri-banner__frame{position:relative;width:100%}
.madri-banner--fixed .madri-banner__frame{height:var(--bn-h,320px)}
.madri-banner__slide{position:relative;width:100%}
.madri-banner--fixed .madri-banner__slide{height:100%}
.madri-banner__img,.madri-moodle-layout .madri-banner__img{display:block !important;width:100% !important;max-width:none !important;height:auto;object-fit:cover}
.madri-banner--fixed .madri-banner__img,.madri-banner--fixed .madri-moodle-layout .madri-banner__img{position:absolute;inset:0;height:100% !important}
.madri-banner__ph{width:100%;min-height:200px;background:#e2e8f0}
.madri-banner--fixed .madri-banner__ph{height:100%;min-height:0}
.madri-banner__copy{position:absolute;z-index:3;top:50%;display:flex;flex-direction:column;align-items:stretch;gap:0;width:min(var(--bn-copy-w,46%),calc(100% - 48px));max-width:calc(100% - 48px);max-height:calc(100% - 32px);padding:var(--bn-pad,28px);border-radius:16px;background:var(--bn-copy-bg,rgba(15,23,42,.55));overflow:hidden;min-width:0}
.madri-banner__copy--left{left:24px;right:auto;transform:translateY(-50%);text-align:left}
.madri-banner__copy--right{right:24px;left:auto;transform:translateY(-50%);text-align:right}
.madri-banner__copy--center{left:50%;right:auto;transform:translate(-50%,-50%);text-align:center}
.madri-banner__title,.madri-banner__sub,.madri-banner__desc,.madri-banner__actions{max-width:100%;min-width:0;overflow-wrap:anywhere;word-break:break-word}
.madri-banner__title{margin:0;font-size:var(--bn-title-size,36px);line-height:1.15;font-weight:700;color:var(--bn-title,#fff) !important}
.madri-banner__sub{margin:0;font-size:var(--bn-sub-size,18px);line-height:1.3;font-weight:600;color:var(--bn-sub,#e2e8f0) !important}
.madri-banner__desc{margin:0;font-size:var(--bn-desc-size,15px);line-height:1.5;color:var(--bn-desc,#f8fafc) !important}
.madri-banner__title + .madri-banner__sub,.madri-banner__title + .madri-banner__desc{margin-top:var(--bn-title-gap,10px)}
.madri-banner__sub + .madri-banner__desc{margin-top:var(--bn-sub-gap,10px)}
.madri-banner__title + .madri-banner__actions,.madri-banner__sub + .madri-banner__actions,.madri-banner__desc + .madri-banner__actions{margin-top:var(--bn-sub-gap,10px)}
.madri-banner__actions{display:flex;flex-wrap:wrap;gap:10px;margin-top:0}
.madri-banner__copy--center .madri-banner__actions{justify-content:center}
.madri-banner__copy--right .madri-banner__actions{justify-content:flex-end}
.madri-banner__btn,.madri-moodle-layout .madri-banner__btn{display:inline-flex;align-items:center;justify-content:center;padding:10px 18px;border-radius:999px;background:var(--bn-btn,#fff);color:var(--bn-btn-text,#0f172a) !important;text-decoration:none !important;font-weight:700;font-size:14px;line-height:1;border:2px solid transparent}
.madri-banner__btn--ghost,.madri-moodle-layout .madri-banner__btn--ghost{background:transparent;color:var(--bn-btn2,#fff) !important;border-color:var(--bn-btn2,#fff)}
.madri-banner__float{position:absolute;z-index:2;pointer-events:none;height:auto}
.madri-banner__float--front{z-index:4}
.madri-banner__float img,.madri-moodle-layout .madri-banner__float img{display:block !important;width:100% !important;max-width:none !important;height:auto !important;object-fit:contain}
.madri-banner__caption{position:absolute;left:0;right:0;bottom:0;z-index:3;padding:10px 16px;text-align:center;color:#475569 !important;font-style:italic;font-size:14px;background:#f8fafc}
.madri-banner__arrow{position:absolute;top:50%;transform:translateY(-50%);z-index:5;width:36px;height:36px;border:0;border-radius:999px;background:var(--bn-accent,#3b82f6);color:#fff;cursor:pointer;font-size:22px;line-height:1;display:flex;align-items:center;justify-content:center}
.madri-banner__arrow--prev{left:10px}
.madri-banner__arrow--next{right:10px}
.madri-banner__dots{position:absolute;left:50%;bottom:12px;transform:translateX(-50%);z-index:5;display:flex;gap:6px}
.madri-banner__dot{width:8px;height:8px;padding:0;border:0;border-radius:999px;background:rgba(255,255,255,.55);cursor:pointer}
.madri-banner__dot.is-active{background:var(--bn-accent,#3b82f6)}
@keyframes madriBannerFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
@keyframes madriBannerSway{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(6deg)}}
@keyframes madriBannerPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}
.madri-banner__motion--float{animation:madriBannerFloat 3.6s ease-in-out infinite}
.madri-banner__motion--sway{animation:madriBannerSway 4.2s ease-in-out infinite}
.madri-banner__motion--pulse{animation:madriBannerPulse 3.2s ease-in-out infinite}
@media (prefers-reduced-motion:reduce){.madri-banner__motion--float,.madri-banner__motion--sway,.madri-banner__motion--pulse{animation:none}}
`.replace(/\s+/g, ' ').trim();

function copyHtml(copy, align) {
  if (!copy.hasOverlay && !copy.title) return '';
  const buttons = [
    copy.btn1Label ? `<a class="madri-banner__btn" href="${escAttr(copy.btn1Link || '#')}">${escText(copy.btn1Label)}</a>` : '',
    copy.btn2Label ? `<a class="madri-banner__btn madri-banner__btn--ghost" href="${escAttr(copy.btn2Link || '#')}">${escText(copy.btn2Label)}</a>` : '',
  ].join('');
  return `<div class="madri-banner__copy madri-banner__copy--${align}">${copy.title ? `<div class="madri-banner__title">${escText(copy.title)}</div>` : ''}${copy.subtitle ? `<div class="madri-banner__sub">${escText(copy.subtitle)}</div>` : ''}${copy.description ? `<div class="madri-banner__desc">${escText(copy.description)}</div>` : ''}${buttons ? `<div class="madri-banner__actions">${buttons}</div>` : ''}</div>`;
}

function floatHtml(item) {
  const motion = item.motion !== 'none' ? ` madri-banner__motion--${item.motion}` : '';
  const layer = item.layer === 'front' ? ' madri-banner__float--front' : '';
  return `<div class="madri-banner__float${layer}" style="left:${item.x}%;top:${item.y}%;width:${item.width}px"><img class="${motion.trim()}" src="${escAttr(item.url)}" alt=""></div>`;
}

function navScript() {
  return `<script>(function(){if(window.madriBannerNav)return;window.madriBannerNav=function(id,idx,total){for(var i=0;i<total;i++){var slide=document.getElementById(id+'_slide_'+i);if(slide)slide.style.display=i===idx?'block':'none';var dot=document.getElementById(id+'_dot_'+i);if(dot){if(i===idx)dot.classList.add('is-active');else dot.classList.remove('is-active');}}};})();</script>`;
}

export function buildBannerCarouselInnerHtml(content, widgetId) {
  const view = bannerView(content);
  const id = `bn_${safeId(widgetId)}`;
  const fixed = view.heightMode === 'fixed';
  const fullClass = view.span === 'full' ? ' madri-banner--full' : '';
  const vars = [
    `--bn-h:${view.heightPx}px`,
    `--bn-accent:${view.accent}`,
    `--bn-copy-w:${view.textWidth}%`,
    `--bn-pad:${view.textPad}px`,
    `--bn-title-gap:${view.titleSubGap}px`,
    `--bn-sub-gap:${view.subDescGap}px`,
    `--bn-radius:${view.radiusPx}px`,
    `--bn-copy-bg:${view.textBg}`,
    `--bn-title-size:${view.titleSize}px`,
    `--bn-title:${view.titleColor}`,
    `--bn-sub-size:${view.subtitleSize}px`,
    `--bn-sub:${view.subtitleColor}`,
    `--bn-desc-size:${view.descSize}px`,
    `--bn-desc:${view.descColor}`,
    `--bn-btn:${view.btnBg}`,
    `--bn-btn-text:${view.btnColor}`,
    `--bn-btn2:${view.btn2Color}`,
  ].join(';');
  const slides = view.slides.map((slide, index) => {
    const copy = slideCopy(slide);
    const media = copy.url
      ? `<img class="madri-banner__img" src="${escAttr(copy.url)}" alt="${escAttr(copy.title || copy.caption || '')}">`
      : '<div class="madri-banner__ph"></div>';
    const caption = copy.caption ? `<div class="madri-banner__caption">${escText(copy.caption)}</div>` : '';
    return `<div class="madri-banner__slide" id="${id}_slide_${index}" style="${index === 0 ? '' : 'display:none'}">${media}${copyHtml(copy, view.align)}${caption}</div>`;
  }).join('');
  const count = view.slides.length;
  const arrows = count > 1
    ? `<button type="button" class="madri-banner__arrow madri-banner__arrow--prev" aria-label="Anterior" onclick="var n=${count},cur=0,i,s;for(i=0;i<n;i++){s=document.getElementById('${id}_slide_'+i);if(s&&s.style.display!=='none')cur=i;}madriBannerNav('${id}',(cur-1+n)%n,n)">‹</button><button type="button" class="madri-banner__arrow madri-banner__arrow--next" aria-label="Próximo" onclick="var n=${count},cur=0,i,s;for(i=0;i<n;i++){s=document.getElementById('${id}_slide_'+i);if(s&&s.style.display!=='none')cur=i;}madriBannerNav('${id}',(cur+1)%n,n)">›</button>`
    : '';
  const dots = count > 1
    ? `<div class="madri-banner__dots">${view.slides.map((_, index) => `<button type="button" class="madri-banner__dot${index === 0 ? ' is-active' : ''}" id="${id}_dot_${index}" aria-label="${index + 1}" onclick="madriBannerNav('${id}',${index},${count})"></button>`).join('')}</div>`
    : '';
  const floats = view.floats.map(floatHtml).join('');
  const classes = `madri-banner${fixed ? ' madri-banner--fixed' : ''}${fullClass}`;
  return `${navScript()}<style>${BANNER_CSS}</style><div id="${id}" class="${classes}" style="${escAttr(vars)}" data-accent="${escAttr(view.accent)}"><div class="madri-banner__frame">${slides}${floats}${arrows}${dots}</div></div>`;
}
